<template>
  <div class="container" id="profile_section">
    <div class="page-header gold mt-4 mb-2">
      <div class="page-header__left">

        <back-btn to="/dashboard" position="relative"></back-btn>

        <h2 class="mb-0">
          <i class="fas fa-users"></i>
          <span v-if="status === 1">Programs</span>
          <span v-if="status === 3">Archived Programs</span>
          (<small v-text="totalRows"></small>)
        </h2>
      </div>
      <div class="page-header__right" v-if="user_level >= 90">
        <a v-if="status === 3" class="archive-link" href="/programs">
          <i class="fas fa-list mr-1"></i>Programs
        </a>
        <a v-if="status === 1" class="archive-link" href="/programs/archive">
          <i class="fas fa-archive mr-1"></i>Archived Programs
        </a>
        <button type="button" class="btn btn-sm btn-add-program" @click="challengeAdd">
          <i class="fas fa-plus mr-1"></i> Add New Program
        </button>
      </div>
    </div>

    <edit-program
      v-if="showEdit"
      :challenge="challenge"
      :statuses="statuses"
      @closed="editModalClosed($event)"
    ></edit-program>

    <!-- Search + pagination controls -->
    <div class="row" id="challenges_wrap">
      <div v-if="totalRows > 2" class="col-md-6 my-1">
        <b-form-group class="mb-0">
          <b-input-group size="sm">
            <b-form-input
              id="filter-input"
              v-model="filter"
              type="search"
              placeholder="Search programs"
            ></b-form-input>
            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>

      <div v-if="totalRows > 2" class="col-md-3 col-4 my-1">
        <b-form-group class="mb-0">
          <b-form-select
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="sm"
          ></b-form-select>
        </b-form-group>
      </div>

      <div v-if="totalRows > 2" class="col-md-3 col-8 my-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="right"
          size="sm"
          class="my-0"
        ></b-pagination>
      </div>

      <!-- Desktop table (admin only) -->
      <div v-if="user_level >= 90" class="col-12 d-none d-md-block">
        <b-table
          bordered
          responsive
          :items="challenges"
          :busy="isBusy"
          :fields="fields"
          :filter="filter"
          :filter-included-fields="filterOn"
          @filtered="onFiltered"
          :current-page="currentPage"
          :per-page="perPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          sort-icon-left
          class="small"
          style="min-height: 165px"
          show-empty
        >
          <template v-slot:table-busy>
            <div class="text-center my-2 p-2">
              <b-spinner class="align-middle"></b-spinner>
              <br />
              <strong>Loading...</strong>
            </div>
          </template>

          <template v-slot:cell(status)="data">
            <span v-text="arrayElmGet(statuses, data.item.status)"></span>
          </template>

          <template v-slot:cell(leaders)="data">
            <span
              v-for="(leader, index) in data.item.leaders"
              v-text="leader.label + (index < data.item.leaders.length - 1 ? ', ' : '')"
              :key="'M_' + leader.id"
              class="mr-1"
            ></span>
          </template>

          <template v-slot:cell(actions)="data">
            <b-dropdown
              v-if="challengeCheckMembership(data.item) || user_level >= 90"
              size="sm"
              split
              split-variant="outline-primary"
              class="gold"
              text="View"
              @click="challengeView(data.item)"
            >
              <b-dropdown-item>
                <a href="#" @click="challengeView(data.item)">View Program</a>
              </b-dropdown-item>
              <b-dropdown-item v-if="user_level >= 90">
                <a href="#" @click="challengeEmail(data.item)">Email Members</a>
              </b-dropdown-item>
              <b-dropdown-item v-if="user_level >= 90">
                <a href="#" @click="challengeEdit(data.item)">Edit</a>
              </b-dropdown-item>
              <b-dropdown-item v-if="user_level >= 90">
                <a href="#" class="text-danger" style="color:#dc3545!important;" @click="deleteProgram(data.item.id)">Delete</a>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </div>

      <!-- Cards (always for non-admins, small screen only for admins) -->
      <div class="col-12" :class="{ 'd-md-none': user_level >= 90 }">
        <div v-if="isBusy" class="text-center my-4 gold">
          <b-spinner class="align-middle"></b-spinner>
          <p class="mt-2"><strong>Loading...</strong></p>
        </div>

        <div v-else-if="mobileFiltered.length === 0" class="text-center my-4 text-muted">
          No programs found.
        </div>

        <div
          v-else
          v-for="item in mobileFiltered"
          :key="item.id"
          class="program-card"
        >
          <div class="program-card__accent"></div>
          <div class="program-card__header">
            <div class="program-card__title">{{ item.title }}</div>
            <span class="program-card__status">{{ arrayElmGet(statuses, item.status) }}</span>
          </div>

          <div v-if="item.leaders && item.leaders.length" class="program-card__leaders">
            <i class="fas fa-user mr-1"></i>
            <span
              v-for="(leader, index) in item.leaders"
              :key="'ML_' + leader.id"
              v-text="leader.label + (index < item.leaders.length - 1 ? ', ' : '')"
            ></span>
          </div>

          <div v-if="item.description" class="program-card__desc">{{ item.description }}</div>

          <div class="program-card__actions" v-if="challengeCheckMembership(item) || user_level >= 90">
            <button class="card-btn card-btn--view" @click="challengeView(item)">
              <i class="fas fa-eye mr-1"></i> View
            </button>
            <button v-if="user_level >= 90" class="card-btn card-btn--edit" @click="challengeEdit(item)">
              <i class="fas fa-pen mr-1"></i> Edit
            </button>
            <button v-if="user_level >= 90" class="card-btn card-btn--email" @click="challengeEmail(item)">
              <i class="fas fa-envelope mr-1"></i> Email
            </button>
            <button v-if="user_level >= 90" class="card-btn card-btn--delete" @click="deleteProgram(item.id)">
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>

      <div v-if="totalRows > 2" class="col-12 my-1">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="right"
          size="sm"
          class="my-0"
        ></b-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import EditProgram from "@/components/programs/EditProgram.vue";
import BackBtn from "@/components/BackBtn.vue";

export default {
  components: {
    BackBtn,
    vSelect,
    EditProgram,
  },
  props: {
    status: Number,
    title: String
  },
  data() {
    return {
      challenge: {},
      showEdit: false,
      statuses: [
        { value: 0, text: 'Closed (deleted)' },
        { value: 1, text: 'Active' },
        { value: 2, text: 'Pending' },
        { value: 3, text: 'Archived' },
      ],
      challenges: null,
      fields: [
        { key: 'actions', sortable: false, label: 'Actions' },
        { key: 'title', sortable: true, label: 'Program' },
        { key: 'leaders', sortable: true, label: 'Program Leader' },
        { key: 'description', sortable: false, label: 'Description' },
        { key: 'status', sortable: true, label: 'Status' },
      ],
      isBusy: true,
      sortBy: 'user_score',
      sortDirection: 'desc',
      sortDesc: true,
      totalRows: 0,
      currentPage: 1,
      perPage: 50,
      pageOptions: [
        { value: 50, text: 'Num to Show' },
        25,
        50,
        100,
        { value: 500, text: 'All' },
      ],
      filter: null,
      filterOn: [],
    };
  },
  computed: {
    active_profile() {
      return this.$store.getters.profileGet;
    },
    user_level() {
      return this.active_profile.level;
    },
    mobileFiltered() {
      if (!this.challenges) return [];
      let result = this.challenges;
      if (this.filter) {
        const f = this.filter.toLowerCase();
        result = result.filter(c =>
          (c.title && c.title.toLowerCase().includes(f)) ||
          (c.description && c.description.toLowerCase().includes(f))
        );
      }
      const start = (this.currentPage - 1) * this.perPage;
      return result.slice(start, start + this.perPage);
    },
  },
  methods: {
    arrayElmGet(obj, value) {
      const entries = Object.entries(obj);
      for (const v of entries) {
        if (parseInt(v[1].value) === parseInt(value)) {
          return v[1].text;
        }
      }
      return null;
    },
    async deleteProgram(id) {
      const proceed = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this program?')
      if (proceed) {
        await axios.delete(`/programs/${id}`)
        this.challengesGet()
      }
    },
    challengeView(item) {
      this.$router.push('/programs/' + item.id);
    },
    challengeEmail(item) {
      this.$router.push('/programs/' + item.id + '/email');
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    challengeAdd() {
      this.challenge = { status: 1, challenge_type: 'cohort-based' }
      this.showEdit = true
    },
    challengeEdit(challenge) {
      this.challenge = challenge;
      this.showEdit = true
    },
    editModalClosed(refresh) {
      this.showEdit = false;
      if (refresh) this.challengesGet();
    },
    challengesGet() {
      this.isBusy = true;
      axios.get('programs', { params: { status: this.status } })
        .then((response) => {
          this.isBusy = false;
          this.challenges = response.data;
          this.totalRows = this.challenges.length;
        })
    },
    challengeCheckMembership(item) {
      return item.members.some(m => m.id === this.$store.state.profile.id)
    },
  },
  mounted() {
    this.challengesGet();
  },
  watch: {},
};
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
}

.page-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.page-header__left h2 {
  font-size: 1.5rem;
}

.page-header__right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

@media (max-width: 576px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .page-header__left h2 {
    font-size: 1.1rem;
  }

  .page-header__right {
    width: 100%;
    justify-content: flex-end;
  }

  .archive-link,
  .btn-add-program {
    font-size: 0.72rem;
    padding: 4px 10px;
  }
}

.archive-link {
  font-size: 0.8rem;
  color: #d6b674;
  text-decoration: none;
  border: 1px solid rgba(214,182,116,0.45);
  border-radius: 20px;
  padding: 4px 12px;
  transition: background 0.18s, color 0.18s;
  white-space: nowrap;
}

.archive-link:hover {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  text-decoration: none;
}

.btn-add-program {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 0.8rem;
  padding: 5px 14px;
  white-space: nowrap;
  transition: opacity 0.18s, transform 0.15s;
}

.btn-add-program:hover {
  opacity: 0.88;
  transform: translateY(-1px);
  color: #fff;
}


/* Mobile program cards */
.program-card {
  position: relative;
  background: #fff;
  border: 1px solid rgba(214, 182, 116, 0.25);
  border-radius: 12px;
  padding: 14px 14px 14px 18px;
  margin-bottom: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  overflow: hidden;
}

.program-card__accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #d6b674 0%, #c4a55a 100%);
  border-radius: 12px 0 0 12px;
}

.program-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
}

.program-card__title {
  font-size: 1rem;
  font-weight: 700;
  color: #222;
  line-height: 1.3;
}

.program-card__status {
  font-size: 0.65rem;
  font-weight: 600;
  background: rgba(214, 182, 116, 0.15);
  color: #c4a55a;
  border: 1px solid rgba(214, 182, 116, 0.35);
  border-radius: 20px;
  padding: 2px 10px;
  white-space: nowrap;
  flex-shrink: 0;
}

.program-card__leaders {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 6px;
}

.program-card__leaders i {
  color: #d6b674;
}

.program-card__desc {
  font-size: 0.78rem;
  color: #888;
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.program-card__actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
}

.card-btn {
  border: none;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 5px 14px;
  cursor: pointer;
  transition: opacity 0.18s, transform 0.15s;
}

.card-btn:active {
  transform: scale(0.96);
}

.card-btn--view {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
}

.card-btn--edit {
  background: #f8f9fa;
  color: #555;
  border: 1px solid #dee2e6;
}

.card-btn--email {
  background: #f8f9fa;
  color: #555;
  border: 1px solid #dee2e6;
}

.card-btn--delete {
  background: rgba(220, 53, 69, 0.08);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.2);
  padding: 5px 10px;
  margin-left: auto;
}
</style>
